import { MfaSetupAtLoginError } from '@/auth/utils/cognitoMfaSetup';

import type { useDomainMatch } from '../hooks/useDomainMatch';
import type { usePendingProjectInvite } from '../hooks/usePendingProjectInvite';
import type { DomainMatch, PendingProjectInvite } from '../types';
import { captureSelfServiceError } from './sentryScrub';

export interface PostVerifyLookupDeps {
  domainMatch: ReturnType<typeof useDomainMatch>;
  pendingProjectInvite: ReturnType<typeof usePendingProjectInvite>;
}

export type PostVerifyLookups =
  | { status: 'resolved'; pendingInvite: PendingProjectInvite | null; domainMatches: DomainMatch[] }
  // ENG-6106: a session refused for MFA setup is terminal, not a lookup
  // failure to recover from.
  | { status: 'refused'; error: MfaSetupAtLoginError }
  | { status: 'failed' };

/**
 * What runs once the email is verified, by a code (EmailOtpStep) or by the
 * provider of a social signup (AccountStep, ENG-6166): the pending project
 * invite and the domain-match query, in parallel (ENG-5334). The domain-match
 * query uses Bearer auth; no OTP token goes on it. The caller dispatches
 * `resolved` to the reducer, which owns the step decision.
 *
 * Promise.allSettled, not Promise.all (review fix #9): these are two
 * independent lookups, so a transient failure on one leg must not throw away
 * a result the other leg already got. A failed leg reads as no invite or no
 * matches; only both failing is `failed`.
 */
export async function runPostVerifyLookups(
  { domainMatch, pendingProjectInvite }: PostVerifyLookupDeps,
  { email, otpToken, step }: { email: string; otpToken?: string; step: 'account' | 'email-otp' }
): Promise<PostVerifyLookups> {
  const [pendingInviteOutcome, domainMatchOutcome] = await Promise.allSettled([
    pendingProjectInvite.fetchPendingInvite(otpToken),
    domainMatch.fetchMatches({ email }),
  ]);
  if (pendingInviteOutcome.status === 'rejected') {
    captureSelfServiceError(pendingInviteOutcome.reason, {
      step,
      substep: 'post_verify_pending_invite_lookup',
    });
  }
  if (domainMatchOutcome.status === 'rejected') {
    captureSelfServiceError(domainMatchOutcome.reason, {
      step,
      substep: 'post_verify_domain_match_lookup',
    });
  }
  const refusal = [pendingInviteOutcome, domainMatchOutcome].find(
    (outcome) => outcome.status === 'rejected' && outcome.reason instanceof MfaSetupAtLoginError
  );
  if (refusal?.status === 'rejected') return { status: 'refused', error: refusal.reason };
  if (pendingInviteOutcome.status === 'rejected' && domainMatchOutcome.status === 'rejected') {
    return { status: 'failed' };
  }
  return {
    status: 'resolved',
    pendingInvite: pendingInviteOutcome.status === 'fulfilled' ? pendingInviteOutcome.value : null,
    domainMatches: domainMatchOutcome.status === 'fulfilled' ? domainMatchOutcome.value : [],
  };
}
